/**
 * Aba "Geral" da Tela A2 — Configurações operacionais do tenant.
 *
 * Expõe 4 campos reais do PATCH `/v1/tenant-config` (R15 — o schema é
 * `.strict()`, campos de identidade do mockup dariam 400):
 *   - checklistAutoFulfillEnabled (toggle)
 *   - checklistAgentPeriodicIntervalMs (select, range 5min–4h)
 *   - defaultInternalNoteVisibility (select, 3 visibilidades)
 *   - authorizedContactsDpoOnly (toggle — Item 350)
 * + metadados read-only (tenantId, última atualização).
 *
 * Escrita travada atrás de `tenant-config-manage` (Opção A — leitura mostra
 * o estado; sem manage, campos somente leitura).
 *
 * Admin Hub T7 — os botões locais Descartar/Salvar saíram; a seção registra
 * na `StickySaveBar` do domínio via `useSectionSave`. O diff campo-a-campo que
 * já existia (antes só reduzido a um booleano `isDirty`) É a fonte — só a
 * apresentação mudou: `diffForm` devolve o PATCH parcial de quem mudou, usado
 * tanto para `save()` (valores novos) quanto para `undo()` (valores antigos,
 * mesma mutation). A mensagem local de sucesso/erro saiu — o toast da barra
 * (que já nomeia os campos alterados) a substitui; ver task-7-report.md.
 * Cabeçalho próprio ("Configurações gerais" + descrição) também saiu: dentro
 * de uma `DomainSection` o título/descrição já vêm do catálogo (item.label/
 * item.efeito) — dois títulos empilhados seria o mesmo problema que o
 * BrandingPage tinha com seu <h1> duplicado.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { useSectionSave } from '../hub/save-bar-context';

import {
  CHECKLIST_INTERVAL_OPTIONS,
  GENERAL_TAB_FIELD_LABELS,
  INTERNAL_NOTE_VISIBILITY_OPTIONS,
  formatUpdatedAt,
} from './config-tenant-labels';
import { useTenantConfig, useUpdateTenantConfig } from './use-tenant-config';

import type {
  InternalNoteVisibilityCode,
  SerializedTenantConfig,
  UpdateTenantConfigRequest,
} from './types';

interface FormState {
  checklistAutoFulfillEnabled: boolean;
  checklistAgentPeriodicIntervalMs: number;
  defaultInternalNoteVisibility: InternalNoteVisibilityCode;
  authorizedContactsDpoOnly: boolean;
  /** Items 478/479 — piso de base do gestor-insight-agent (conversas em 30d). */
  insightMinBasedOnCount: number;
}

function toForm(c: SerializedTenantConfig): FormState {
  return {
    checklistAutoFulfillEnabled: c.checklistAutoFulfillEnabled,
    checklistAgentPeriodicIntervalMs: c.checklistAgentPeriodicIntervalMs,
    defaultInternalNoteVisibility: c.defaultInternalNoteVisibility,
    authorizedContactsDpoOnly: c.authorizedContactsDpoOnly,
    insightMinBasedOnCount: c.insightMinBasedOnCount ?? 10,
  };
}

/**
 * Diff campo-a-campo de `next` contra `prev` — devolve só os que mudaram, com
 * o valor de `next`. Usado nos dois sentidos: `diffForm(form, serverForm)`
 * (o que enviar) e `diffForm(serverForm, form)` (os valores antigos, para o
 * `undo`).
 */
function diffForm(next: FormState, prev: FormState): UpdateTenantConfigRequest {
  // `UpdateTenantConfigRequest` tem campos `readonly` (PATCH parcial do
  // engine) — construído por spread incremental, não por atribuição.
  return {
    ...(next.checklistAutoFulfillEnabled !== prev.checklistAutoFulfillEnabled && {
      checklistAutoFulfillEnabled: next.checklistAutoFulfillEnabled,
    }),
    ...(next.checklistAgentPeriodicIntervalMs !== prev.checklistAgentPeriodicIntervalMs && {
      checklistAgentPeriodicIntervalMs: next.checklistAgentPeriodicIntervalMs,
    }),
    ...(next.defaultInternalNoteVisibility !== prev.defaultInternalNoteVisibility && {
      defaultInternalNoteVisibility: next.defaultInternalNoteVisibility,
    }),
    ...(next.authorizedContactsDpoOnly !== prev.authorizedContactsDpoOnly && {
      authorizedContactsDpoOnly: next.authorizedContactsDpoOnly,
    }),
    ...(next.insightMinBasedOnCount !== prev.insightMinBasedOnCount && {
      insightMinBasedOnCount: next.insightMinBasedOnCount,
    }),
  };
}

function changedLabelsOf(patch: UpdateTenantConfigRequest): string[] {
  return (Object.keys(patch) as (keyof typeof GENERAL_TAB_FIELD_LABELS)[]).map(
    (k) => GENERAL_TAB_FIELD_LABELS[k],
  );
}

export function GeneralTab({ canManage }: { readonly canManage: boolean }): JSX.Element {
  const query = useTenantConfig();
  const update = useUpdateTenantConfig();

  const [form, setForm] = useState<FormState | null>(null);

  // Sincroniza o form local com os dados do servidor quando chegam/mudam.
  useEffect(() => {
    if (query.data !== undefined) {
      setForm(toForm(query.data));
    }
  }, [query.data]);

  const serverForm = useMemo(
    () => (query.data !== undefined ? toForm(query.data) : null),
    [query.data],
  );

  const isDirty = useMemo(() => {
    if (form === null || serverForm === null) return false;
    return (
      form.checklistAutoFulfillEnabled !== serverForm.checklistAutoFulfillEnabled ||
      form.checklistAgentPeriodicIntervalMs !== serverForm.checklistAgentPeriodicIntervalMs ||
      form.defaultInternalNoteVisibility !== serverForm.defaultInternalNoteVisibility ||
      form.authorizedContactsDpoOnly !== serverForm.authorizedContactsDpoOnly ||
      form.insightMinBasedOnCount !== serverForm.insightMinBasedOnCount
    );
  }, [form, serverForm]);

  // Diff estruturado (não só o booleano `isDirty`) — fonte de `changedLabels`
  // e do payload de `save`/`undo`. Precisa ser calculado ANTES dos returns
  // condicionais de loading/erro abaixo (regra dos hooks: `useSectionSave` é
  // chamado incondicionalmente em todo render).
  const forwardDiff = useMemo(
    () => (form !== null && serverForm !== null ? diffForm(form, serverForm) : {}),
    [form, serverForm],
  );
  const changedLabels = useMemo(() => changedLabelsOf(forwardDiff), [forwardDiff]);
  const previousValues = useRef<UpdateTenantConfigRequest | null>(null);

  const handleDiscard = (): void => {
    if (serverForm !== null) setForm(serverForm);
    update.reset();
  };

  useSectionSave(
    isDirty && canManage && form !== null && serverForm !== null
      ? {
          sectionId: 'geral',
          changedLabels,
          save: async () => {
            // Captura os valores ANTIGOS dos campos que vão mudar — mesma
            // mutation reaplica isto no `undo`.
            previousValues.current = diffForm(serverForm, form);
            await update.mutateAsync(forwardDiff);
          },
          discard: handleDiscard,
          undo: async () => {
            if (previousValues.current !== null) {
              await update.mutateAsync(previousValues.current);
            }
          },
        }
      : null,
  );

  if (query.isLoading) {
    return <p className="text-sm text-text-secondary">Carregando configurações…</p>;
  }
  if (query.isError || query.data === undefined || form === null) {
    return <p className="text-sm text-red-600">Falha ao carregar as configurações do tenant.</p>;
  }

  const fieldsDisabled = !canManage || update.isPending;

  return (
    <div className="max-w-2xl">
      <div className="space-y-6">
        {/* Auto-fulfill de checklist */}
        <div className="flex items-start justify-between gap-4 rounded-xl border border-border-subtle bg-surface p-4">
          <div className="min-w-0">
            <label htmlFor="cfg-autofulfill" className="text-sm font-medium text-text-primary">
              Auto-preenchimento do checklist
            </label>
            <p className="mt-1 text-xs text-text-secondary">
              Quando ligado, o checklist-agent efetiva automaticamente os items que propõe, com
              registro em audit. Desligado, ele apenas sugere e um atendente confirma no banner.
            </p>
          </div>
          <button
            id="cfg-autofulfill"
            type="button"
            role="switch"
            aria-checked={form.checklistAutoFulfillEnabled}
            aria-label="Auto-preenchimento do checklist"
            disabled={fieldsDisabled}
            onClick={() =>
              setForm((f) =>
                f === null
                  ? f
                  : { ...f, checklistAutoFulfillEnabled: !f.checklistAutoFulfillEnabled },
              )
            }
            className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
              form.checklistAutoFulfillEnabled ? 'bg-accent' : 'bg-border-subtle'
            }`}
          >
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                form.checklistAutoFulfillEnabled ? 'translate-x-[22px]' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {/* Intervalo periódico do checklist-agent */}
        <div className="rounded-xl border border-border-subtle bg-surface p-4">
          <label htmlFor="cfg-interval" className="text-sm font-medium text-text-primary">
            Intervalo do agente de checklist
          </label>
          <p className="mt-1 mb-3 text-xs text-text-secondary">
            Com que frequência o checklist-agent revisa as conversas (5 min a 4 horas).
          </p>
          <select
            id="cfg-interval"
            aria-label="Intervalo do agente de checklist"
            disabled={fieldsDisabled}
            value={form.checklistAgentPeriodicIntervalMs}
            onChange={(e) =>
              setForm((f) =>
                f === null ? f : { ...f, checklistAgentPeriodicIntervalMs: Number(e.target.value) },
              )
            }
            className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none disabled:opacity-50"
          >
            {CHECKLIST_INTERVAL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
            {/* Se o valor do servidor não estiver nas opções, mantém visível. */}
            {!CHECKLIST_INTERVAL_OPTIONS.some(
              (o) => o.value === form.checklistAgentPeriodicIntervalMs,
            ) && (
              <option value={form.checklistAgentPeriodicIntervalMs}>
                {Math.round(form.checklistAgentPeriodicIntervalMs / 60000)} minutos
              </option>
            )}
          </select>
        </div>

        {/* Items 478/479 — piso de base dos insights de gestão */}
        <div className="rounded-xl border border-border-subtle bg-surface p-4">
          <label htmlFor="cfg-insight-min-basis" className="text-sm font-medium text-text-primary">
            Mínimo de conversas para insights de gestão
          </label>
          <p className="mt-1 mb-3 text-xs text-text-secondary">
            Abaixo deste número de conversas nos últimos 30 dias, o agente de insights não roda e os
            insights existentes aparecem marcados como «base insuficiente», sem confiança.
          </p>
          <input
            id="cfg-insight-min-basis"
            type="number"
            inputMode="numeric"
            min={1}
            max={10000}
            step={1}
            aria-label="Mínimo de conversas para insights de gestão"
            disabled={fieldsDisabled}
            value={form.insightMinBasedOnCount}
            onChange={(e) => {
              const n = Math.trunc(Number(e.target.value));
              setForm((f) =>
                f === null || !Number.isFinite(n) || n < 1
                  ? f
                  : { ...f, insightMinBasedOnCount: Math.min(n, 10_000) },
              );
            }}
            className="w-40 rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none disabled:opacity-60"
          />
        </div>

        {/* Visibilidade padrão de nota interna */}
        <div className="rounded-xl border border-border-subtle bg-surface p-4">
          <label htmlFor="cfg-note-visibility" className="text-sm font-medium text-text-primary">
            Visibilidade padrão de notas internas
          </label>
          <p className="mt-1 mb-3 text-xs text-text-secondary">
            {INTERNAL_NOTE_VISIBILITY_OPTIONS.find(
              (o) => o.value === form.defaultInternalNoteVisibility,
            )?.hint ?? 'Visibilidade aplicada a notas internas criadas sem visibilidade explícita.'}
          </p>
          <select
            id="cfg-note-visibility"
            aria-label="Visibilidade padrão de notas internas"
            disabled={fieldsDisabled}
            value={form.defaultInternalNoteVisibility}
            onChange={(e) =>
              setForm((f) =>
                f === null
                  ? f
                  : {
                      ...f,
                      defaultInternalNoteVisibility: e.target.value as InternalNoteVisibilityCode,
                    },
              )
            }
            className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none disabled:opacity-50"
          >
            {INTERNAL_NOTE_VISIBILITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Item 350 — gating por nicho dos contatos autorizados do Bond (LGPD) */}
        <div className="flex items-start justify-between gap-4 rounded-xl border border-border-subtle bg-surface p-4">
          <div className="min-w-0">
            <label
              htmlFor="cfg-contacts-dpo-only"
              className="text-sm font-medium text-text-primary"
            >
              Contatos autorizados: somente DPO
            </label>
            <p className="mt-1 text-xs text-text-secondary">
              Quando ligado (nichos regulados — saúde/jurídico), apenas o DPO gerencia os contatos
              autorizados do vínculo (LGPD). Desligado (padrão), atendentes e admin também gerenciam
              no atendimento.
            </p>
          </div>
          <button
            id="cfg-contacts-dpo-only"
            type="button"
            role="switch"
            aria-checked={form.authorizedContactsDpoOnly}
            aria-label="Contatos autorizados somente DPO"
            disabled={fieldsDisabled}
            onClick={() =>
              setForm((f) =>
                f === null ? f : { ...f, authorizedContactsDpoOnly: !f.authorizedContactsDpoOnly },
              )
            }
            className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
              form.authorizedContactsDpoOnly ? 'bg-accent' : 'bg-border-subtle'
            }`}
          >
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                form.authorizedContactsDpoOnly ? 'translate-x-[22px]' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {/* Metadados read-only */}
        <dl className="grid grid-cols-2 gap-4 rounded-xl border border-border-subtle bg-app-bg p-4 text-xs">
          <div>
            <dt className="text-text-secondary">ID do tenant</dt>
            <dd className="mt-0.5 font-mono text-text-primary">{query.data.tenantId}</dd>
          </div>
          <div>
            <dt className="text-text-secondary">Última atualização</dt>
            <dd className="mt-0.5 text-text-primary">{formatUpdatedAt(query.data.updatedAt)}</dd>
          </div>
        </dl>
      </div>

      {/* Descartar/Salvar saíram — vivem na StickySaveBar do domínio (Admin Hub
          T7); sucesso/erro do save viram toast da barra, não mensagem local. */}
      {!canManage && (
        <p className="mt-6 rounded-lg border border-border-subtle bg-app-bg px-4 py-3 text-sm text-text-secondary">
          Você tem acesso de leitura. A edição destas configurações é restrita ao administrador do
          tenant.
        </p>
      )}
    </div>
  );
}
